import { useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import DialogContentText from '@mui/material/DialogContentText';

import { useTranslate } from 'src/locales';
import { ICandidateApplication } from 'src/services/candidates/candidates.types';
import { ProfilePushSection } from 'src/services/applications/applications.types';

import { PROFILE_PUSH_SECTION_LABEL_KEY } from './profile-push.utils';

/** A single section saved on the record, or a whole resume import with a choice of sections. */
export type ProfilePushMode = 'section' | 'import';

export type ProfilePushSelection = {
  applicationIds: string[];
  sections: ProfilePushSection[];
};

type Props = {
  open: boolean;
  mode: ProfilePushMode;
  sections: ProfilePushSection[];
  applications: ICandidateApplication[];
  loading?: boolean;
  onConfirm: (selection: ProfilePushSelection) => void;
  onCancel: () => void;
};

const clientName = (application: ICandidateApplication) =>
  application.job?.client_company_name || application.job?.client?.company_name;

// JT1-1808: offers to copy a change on the candidate record onto the candidate's live applications.
// Every application that can still change starts ticked; one already sent to the client is listed
// without a tick box. After a resume import the recruiter also picks which sections to copy.
export default function ProfilePushDialog({ open, mode, sections, applications, loading, onConfirm, onCancel }: Props) {
  const { t } = useTranslate();

  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [selectedSections, setSelectedSections] = useState<ProfilePushSection[]>([]);

  useEffect(() => {
    // whenever the dialog opens on a fresh change, start with everything that can change ticked
    if (!open) return;
    setSelectedApplications(applications.filter((application) => !application.profile_locked).map((application) => application.id));
    setSelectedSections(sections);
  }, [open, applications, sections]);

  const toggleApplication = (id: string) => {
    setSelectedApplications((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleSection = (section: ProfilePushSection) => {
    setSelectedSections((prev) => (prev.includes(section) ? prev.filter((item) => item !== section) : [...prev, section]));
  };

  const sectionNames = sections.map((section) => t(PROFILE_PUSH_SECTION_LABEL_KEY[section])).join(', ');

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('candidates.profile_push.title')}</DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {mode === 'import'
            ? t('candidates.profile_push.import_subtitle')
            : t('candidates.profile_push.section_subtitle', { sections: sectionNames })}
        </DialogContentText>

        {mode === 'import' && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('candidates.profile_push.sections_label')}
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {sections.map((section) => (
                <FormControlLabel
                  key={section}
                  control={
                    <Checkbox
                      checked={selectedSections.includes(section)}
                      onChange={() => toggleSection(section)}
                    />
                  }
                  label={t(PROFILE_PUSH_SECTION_LABEL_KEY[section])}
                />
              ))}
            </FormGroup>
          </>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t('candidates.profile_push.applications_label')}
        </Typography>
        <FormGroup>
          {applications.map((application) => (
            <FormControlLabel
              key={application.id}
              sx={{ alignItems: 'flex-start', mb: 1 }}
              disabled={application.profile_locked}
              control={
                <Checkbox
                  checked={selectedApplications.includes(application.id)}
                  onChange={() => toggleApplication(application.id)}
                  inputProps={{ 'aria-label': application.job?.title }}
                />
              }
              label={
                <Stack sx={{ pt: 1 }}>
                  <Typography variant="subtitle2">{application.job?.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {[clientName(application), t(`enums.application_stage.${application.stage}`)].filter(Boolean).join(' · ')}
                  </Typography>
                  {application.profile_locked && (
                    <Typography variant="caption" color="text.secondary">
                      {t('candidates.profile_push.locked')}
                    </Typography>
                  )}
                </Stack>
              }
            />
          ))}
        </FormGroup>

        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('candidates.profile_push.warning')}
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={onCancel} disabled={loading}>
          {t('candidates.profile_push.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={selectedApplications.length === 0 || selectedSections.length === 0}
          onClick={() => onConfirm({ applicationIds: selectedApplications, sections: selectedSections })}
        >
          {t('candidates.profile_push.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
